import { createStore, createEffect, createEvent, combine } from 'effector';
import { $accountByIdStore } from '../accounts';
import { $cardStore } from '../card';
import { $notificationStore } from '../notificationStore';
import {
  AlertsApi,
  type AddDeclinedAuthRequest,
  type AlertSubscriptionResponse,
  type DeleteDeclinedAuthRequest,
  type IAlertsUpdate,
  type Recipient,
  type UpdateDeclinedAuthRequest,
} from '@shared/api';

const addDeclinedAuthEffect = createEffect((request: AddDeclinedAuthRequest) => AlertsApi.addDeclinedAuth(request));
const updateDeclinedAuthEffect = createEffect((request: IAlertsUpdate<UpdateDeclinedAuthRequest>) => AlertsApi.updateDeclinedAuth(request));
const deleteDeclinedAuthEffect = createEffect((request: DeleteDeclinedAuthRequest) => AlertsApi.deleteSubscription(request));

const getRecipientEvent = createEvent<Recipient[]>();
const addRecipientEvent = createEvent<Recipient>();
const removeRecipientEvent = createEvent<string>();
const clearEvent = createEvent();

addDeclinedAuthEffect.done.watch((result) => {
  if (result.result!.data?.accountId != null) {
    const id = result.result!.data?.accountId;
    $accountByIdStore.events.getAccount({ id });
  }
  if (result.result!.data?.cardId != null) {
    const id = result.result!.data?.cardId;
    $cardStore.events.getCard({ id });
  }
});

updateDeclinedAuthEffect.done.watch((result) => {
  if (result.result!.data?.accountId != null) {
    const id = result.result!.data?.accountId;
    $accountByIdStore.events.getAccount({ id });
  }
  if (result.result!.data?.cardId != null) {
    const id = result.result!.data?.cardId;
    $cardStore.events.getCard({ id });
  }
});

deleteDeclinedAuthEffect.done.watch((result) => {
  if (result.params.accountId !== undefined) {
    const id = result.params.accountId;
    $accountByIdStore.events.getAccount({ id });
  }
  if (result.params.cardId !== undefined) {
    const id = result.params.cardId;
    $cardStore.events.getCard({ id });
  }
});

const $dataStore = createStore<AlertSubscriptionResponse | null>(null)
  .on(addDeclinedAuthEffect.done, (_, data) => data.result.data! || null)
  .on(deleteDeclinedAuthEffect.done, () => null)
  .reset(clearEvent);

const $recipientStore = createStore<Recipient[]>([])
  .on(addRecipientEvent, (state, recipient) => [...state, recipient])
  .on(removeRecipientEvent, (state, email) => state.filter((recipient) => recipient.email !== email))
  .on(getRecipientEvent, (_, newRecipient) => newRecipient)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(addDeclinedAuthEffect.pending, (_, data) => data)
  .on(addDeclinedAuthEffect.fail, () => false)
  .on(addDeclinedAuthEffect.done, () => false)
  .on(updateDeclinedAuthEffect.pending, (_, data) => data)
  .on(updateDeclinedAuthEffect.fail, () => false)
  .on(updateDeclinedAuthEffect.done, () => false)
  .on(deleteDeclinedAuthEffect.pending, (_, data) => data)
  .on(deleteDeclinedAuthEffect.fail, () => false)
  .on(deleteDeclinedAuthEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(addDeclinedAuthEffect, () => null)
  // .on(addDeclinedAuthEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addDeclinedAuthEffect.fail, () => 'Something went wrong while adding Declined Authorizations alert. Please try again.')
  .on(addDeclinedAuthEffect.done, () => null)
  .on(updateDeclinedAuthEffect, () => null)
  // .on(updateDeclinedAuthEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(updateDeclinedAuthEffect.fail, () => 'Something went wrong while updating Declined Authorizations alert. Please try again.')
  .on(updateDeclinedAuthEffect.done, () => null)
  .on(deleteDeclinedAuthEffect, () => null)
  // .on(deleteDeclinedAuthEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteDeclinedAuthEffect.fail, () => 'Something went wrong while declining Declined Authorizations alert. Please try again.')
  .on(deleteDeclinedAuthEffect.done, () => null)
  .reset(clearEvent);
$errorStore.watch((error) => $notificationStore.events.setError(error));

export const $declinedAuthStore = {
  events: {
    addDeclinedAuth: addDeclinedAuthEffect,
    updateDeclinedAuth: updateDeclinedAuthEffect,
    deleteDeclinedAuth: deleteDeclinedAuthEffect,
    getRecipient: getRecipientEvent,
    addRecipient: addRecipientEvent,
    removeRecipient: removeRecipientEvent,
    clear: clearEvent,
  },
  store: combine({
    loading: $loadingStore,
    data: $dataStore,
    recipient: $recipientStore,
    error: $errorStore,
  }),
};
